'use client';

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type * as React from 'react';

// gloss
type SortableListProps = {
  ids: string[];
  onReorder: (nextIds: string[], activeId: string) => void;
  orientation?: 'vertical' | 'horizontal';
  children: React.ReactNode;
};

// gloss
export const reorderIds = (ids: string[], activeId: string, overId: string): string[] => {
  if (activeId === overId) return ids;
  const activeIndex = ids.indexOf(activeId);
  const overIndex = ids.indexOf(overId);
  if (activeIndex === -1 || overIndex === -1) return ids;
  return arrayMove(ids, activeIndex, overIndex);
};

// gloss
export const SortableList = ({ ids, onReorder, orientation = 'vertical', children }: SortableListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const isHorizontal = orientation === 'horizontal';
  const strategy = isHorizontal ? horizontalListSortingStrategy : verticalListSortingStrategy;
  const modifiers = isHorizontal ? [restrictToHorizontalAxis] : [restrictToVerticalAxis, restrictToParentElement];

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const activeId = String(active.id);
    const nextIds = reorderIds(ids, activeId, String(over.id));
    if (nextIds !== ids) onReorder(nextIds, activeId);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={modifiers} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={strategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};
